import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { MessageSquare, User, LogOut, ShoppingBag, Package, Shield } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-[#0B1120]/80 backdrop-blur-2xl border-b border-white/5 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-md flex items-center justify-center">
              <Package className="w-5 h-5 text-[#0B1120]" />
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">GlobalMinerX</span>
          </Link>

          <div className="flex items-center space-x-3">
            <LanguageSelector />
            
            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/messages')}
                  data-testid="messages-button"
                  className="text-[#94A3B8] hover:text-white hover:bg-white/5"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('nav.messages')}
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="user-menu-button" className="text-[#94A3B8] hover:text-white hover:bg-white/5">
                      <User className="w-4 h-4 mr-2" />
                      {user.name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#121A2F] border-[#1E293B]">
                    <DropdownMenuItem
                      onClick={() => navigate(`/${user.role}`)}
                      data-testid="dashboard-link"
                      className="text-[#94A3B8] hover:text-white focus:text-white focus:bg-white/5"
                    >
                      {user.role === 'buyer' && <ShoppingBag className="w-4 h-4 mr-2" />}
                      {user.role === 'seller' && <Package className="w-4 h-4 mr-2" />}
                      {user.role === 'admin' && <Shield className="w-4 h-4 mr-2" />}
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleLogout}
                      data-testid="logout-button"
                      className="text-[#94A3B8] hover:text-white focus:text-white focus:bg-white/5"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/auth')}
                  data-testid="login-button"
                  className="text-[#94A3B8] hover:text-white hover:bg-white/5"
                >
                  {t('nav.login')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigate('/auth')}
                  data-testid="signup-button"
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold"
                >
                  {t('nav.signup')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
